import React, { useState, useCallback } from 'react';
import TenantConfigService from '../../services/TenantConfigService';

/**
 * Schedule Pattern CSV Uploader
 * 
 * Access this page directly at: /admin/upload-schedule-patterns
 * Not included in main navigation - admin utility page only
 * 
 * Expected CSV format:
 * Pattern_ID, Pattern_Name, Num_Periods, Period_1_ID, Period_1_Name, Period_1_Start, Period_1_End, ...
 * 
 * Example:
 * pattern_1, Pattern 1, 3, p1, Block 1, 08:20, 12:25, p2, Block 2, 12:30, 12:55, p3, Block 3, 13:00, 16:00
 */

const SchedulePatternUploader = ({ currentUser }) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [previewMode, setPreviewMode] = useState(true);
  const [selectedPatterns, setSelectedPatterns] = useState(new Set());
  const [parseError, setParseError] = useState(null);

  const tenantId = currentUser?.tenantId;

  // Normalize time to HH:MM format (e.g., "9:00" -> "09:00")
  const normalizeTime = (timeStr) => {
    if (!timeStr) return '';
    const cleaned = timeStr.trim().replace(/"/g, '');
    if (!cleaned) return '';
    
    // Handle HH:MM format
    const parts = cleaned.split(':');
    if (parts.length === 2) {
      const hours = parts[0].padStart(2, '0');
      const minutes = parts[1].padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    
    return cleaned;
  };

  // Parse CSV file
  const parseCSV = useCallback((text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('CSV must have a header row and at least one data row');
    }

    // Parse header
    const header = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    
    // Find column indices
    const patternIdIdx = header.findIndex(h => h.toLowerCase().includes('pattern_id'));
    const patternNameIdx = header.findIndex(h => h.toLowerCase().includes('pattern_name'));
    const numPeriodsIdx = header.findIndex(h => h.toLowerCase().includes('num_periods'));

    if (patternIdIdx === -1 || patternNameIdx === -1) {
      throw new Error('CSV must have Pattern_ID and Pattern_Name columns');
    }

    // Find period columns
    const periodColumns = [];
    let periodNum = 1;
    while (true) {
      const idIdx = header.findIndex(h => h.toLowerCase() === `period_${periodNum}_id`);
      const nameIdx = header.findIndex(h => h.toLowerCase() === `period_${periodNum}_name`);
      const startIdx = header.findIndex(h => h.toLowerCase() === `period_${periodNum}_start`);
      const endIdx = header.findIndex(h => h.toLowerCase() === `period_${periodNum}_end`);
      
      if (startIdx === -1 || endIdx === -1) break;
      
      periodColumns.push({ idIdx, nameIdx, startIdx, endIdx, num: periodNum });
      periodNum++;
    }

    // Parse data rows
    const patterns = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;

      // Handle quoted values with commas
      const values = [];
      let current = '';
      let inQuotes = false;
      for (const char of line) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      const patternId = values[patternIdIdx]?.replace(/"/g, '');
      const patternName = values[patternNameIdx]?.replace(/"/g, '');
      
      if (!patternId || !patternName) continue;

      // Parse periods
      const periods = [];
      for (const pc of periodColumns) {
        const startTime = normalizeTime(values[pc.startIdx]);
        const endTime = normalizeTime(values[pc.endIdx]);
        
        if (startTime && endTime) {
          periods.push({
            id: values[pc.idIdx]?.replace(/"/g, '') || `p${pc.num}`,
            name: values[pc.nameIdx]?.replace(/"/g, '') || `Block ${pc.num}`,
            start_time: startTime,
            end_time: endTime
          });
        }
      }

      if (periods.length > 0) {
        patterns.push({
          id: patternId,
          name: patternName,
          isDefault: i === 1, // First pattern is default
          periods
        });
      }
    }

    return patterns;
  }, []);

  // Handle file selection
  const handleFileChange = useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setParseError(null);
    setUploadStatus(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const patterns = parseCSV(event.target.result);
        setParsedData(patterns);
        setSelectedPatterns(new Set(patterns.map(p => p.id)));
      } catch (err) {
        setParseError(err.message);
        setParsedData([]);
      }
    };
    reader.readAsText(selectedFile);
  }, [parseCSV]);

  // Toggle pattern selection
  const togglePattern = (patternId) => {
    const newSelected = new Set(selectedPatterns);
    if (newSelected.has(patternId)) {
      newSelected.delete(patternId);
    } else {
      newSelected.add(patternId);
    }
    setSelectedPatterns(newSelected);
  };

  // Select/deselect all
  const toggleAll = () => {
    if (selectedPatterns.size === parsedData.length) {
      setSelectedPatterns(new Set());
    } else {
      setSelectedPatterns(new Set(parsedData.map(p => p.id)));
    }
  };

  // Upload to Firestore
  const handleUpload = async () => {
    if (!tenantId) {
      setUploadStatus({ type: 'error', message: 'No tenant ID found. Please log in again.' });
      return;
    }

    const patternsToUpload = parsedData.filter(p => selectedPatterns.has(p.id));
    if (patternsToUpload.length === 0) {
      setUploadStatus({ type: 'error', message: 'No patterns selected for upload.' });
      return;
    }

    setUploading(true);
    setUploadStatus(null);

    try {
      // Get existing schedule_types config
      const existingConfig = await TenantConfigService.getConfig(tenantId, 'schedule_types', { 
        scheduleTypes: [], 
        customFields: [] 
      });
      
      const existingTypes = existingConfig.scheduleTypes || [];

      // Merge - update existing patterns by ID, add new ones
      const updatedTypes = [...existingTypes];

      for (const pattern of patternsToUpload) {
        const existingIndex = updatedTypes.findIndex(t => t.id === pattern.id);
        if (existingIndex >= 0) {
          updatedTypes[existingIndex] = pattern;
        } else {
          updatedTypes.push(pattern);
        }
      }

      // Save using TenantConfigService
      const success = await TenantConfigService.updateConfig(tenantId, 'schedule_types', {
        scheduleTypes: updatedTypes,
        customFields: existingConfig.customFields || []
      });

      if (success) {
        setUploadStatus({
          type: 'success',
          message: `Successfully uploaded ${patternsToUpload.length} schedule patterns!`
        });
      } else {
        throw new Error('Update returned false');
      }

    } catch (err) {
      console.error('Upload error:', err);
      setUploadStatus({
        type: 'error',
        message: `Upload failed: ${err.message}`
      });
    } finally {
      setUploading(false);
    }
  };

  // Format time for display
  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h > 12 ? h - 12 : (h === 0 ? 12 : h);
    return `${displayHour}:${minutes} ${ampm}`;
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>📁 Schedule Pattern Uploader</h1>
        <p style={styles.subtitle}>
          Upload CSV file to bulk import schedule patterns into Relief Roster
        </p>
      </div>

      {/* Instructions */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>📋 CSV Format Requirements</h3>
        <div style={styles.instructions}>
          <p>Your CSV file should have the following columns:</p>
          <code style={styles.codeBlock}>
            Pattern_ID, Pattern_Name, Num_Periods, Period_1_ID, Period_1_Name, Period_1_Start, Period_1_End, Period_2_ID, ...
          </code>
          <p style={{ marginTop: '12px' }}>
            <strong>Time format:</strong> 24-hour format (e.g., 08:20, 13:00, 16:00)
          </p>
        </div>
      </div>

      {/* File Upload */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>📤 Select File</h3>
        <div style={styles.uploadArea}>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={styles.fileInput}
            id="csv-upload"
          />
          <label htmlFor="csv-upload" style={styles.uploadLabel}>
            {file ? (
              <>
                <span style={styles.fileName}>✅ {file.name}</span>
                <span style={styles.fileSize}>({(file.size / 1024).toFixed(1)} KB)</span>
              </>
            ) : (
              <>
                <span style={styles.uploadIcon}>📁</span>
                <span>Click to select CSV file or drag and drop</span>
              </>
            )}
          </label>
        </div>

        {parseError && (
          <div style={styles.errorMessage}>
            ❌ {parseError}
          </div>
        )}
      </div>

      {/* Preview */}
      {parsedData.length > 0 && (
        <div style={styles.card}>
          <div style={styles.previewHeader}>
            <h3 style={styles.cardTitle}>
              👁️ Preview ({parsedData.length} patterns found)
            </h3>
            <div style={styles.previewActions}>
              <button
                onClick={toggleAll}
                style={styles.selectAllBtn}
              >
                {selectedPatterns.size === parsedData.length ? 'Deselect All' : 'Select All'}
              </button>
              <span style={styles.selectedCount}>
                {selectedPatterns.size} selected
              </span>
            </div>
          </div>

          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Select</th>
                  <th style={styles.th}>Pattern ID</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Periods</th>
                  <th style={styles.th}>Time Blocks</th>
                </tr>
              </thead>
              <tbody>
                {parsedData.slice(0, previewMode ? 20 : parsedData.length).map((pattern) => (
                  <tr key={pattern.id} style={styles.tr}>
                    <td style={styles.td}>
                      <input
                        type="checkbox"
                        checked={selectedPatterns.has(pattern.id)}
                        onChange={() => togglePattern(pattern.id)}
                        style={styles.checkbox}
                      />
                    </td>
                    <td style={styles.td}>
                      <code style={styles.patternId}>{pattern.id}</code>
                    </td>
                    <td style={styles.td}>{pattern.name}</td>
                    <td style={styles.td}>{pattern.periods.length}</td>
                    <td style={styles.td}>
                      <div style={styles.periodsList}>
                        {pattern.periods.map((p, idx) => (
                          <span key={idx} style={styles.periodBadge}>
                            {formatTime(p.start_time)} - {formatTime(p.end_time)}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {parsedData.length > 20 && (
            <button
              onClick={() => setPreviewMode(!previewMode)}
              style={styles.showMoreBtn}
            >
              {previewMode ? `Show all ${parsedData.length} patterns` : 'Show less'}
            </button>
          )}
        </div>
      )}

      {/* Upload Button */}
      {parsedData.length > 0 && (
        <div style={styles.uploadSection}>
          <button
            onClick={handleUpload}
            disabled={uploading || selectedPatterns.size === 0}
            style={{
              ...styles.uploadBtn,
              ...(uploading || selectedPatterns.size === 0 ? styles.uploadBtnDisabled : {})
            }}
          >
            {uploading ? (
              <>⏳ Uploading...</>
            ) : (
              <>🚀 Upload {selectedPatterns.size} Patterns to Firestore</>
            )}
          </button>

          {uploadStatus && (
            <div style={{
              ...styles.statusMessage,
              ...(uploadStatus.type === 'success' ? styles.successMessage : styles.errorMessage)
            }}>
              {uploadStatus.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a202c',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '16px',
    color: '#718096',
    margin: 0,
  },
  card: {
    background: '#ffffff',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e2e8f0',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2d3748',
    margin: '0 0 16px 0',
  },
  instructions: {
    fontSize: '14px',
    color: '#4a5568',
    lineHeight: '1.6',
  },
  codeBlock: {
    display: 'block',
    background: '#f7fafc',
    padding: '12px 16px',
    borderRadius: '6px',
    fontSize: '12px',
    color: '#2d3748',
    overflowX: 'auto',
    border: '1px solid #e2e8f0',
  },
  uploadArea: {
    position: 'relative',
  },
  fileInput: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    opacity: 0,
    cursor: 'pointer',
  },
  uploadLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    border: '2px dashed #cbd5e0',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    color: '#718096',
    fontSize: '14px',
    gap: '8px',
  },
  uploadIcon: {
    fontSize: '32px',
  },
  fileName: {
    color: '#2b6cb0',
    fontWeight: '600',
  },
  fileSize: {
    color: '#a0aec0',
    fontSize: '12px',
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
  },
  previewActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  selectAllBtn: {
    padding: '6px 12px',
    fontSize: '13px',
    background: '#edf2f7',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#4a5568',
  },
  selectedCount: {
    fontSize: '13px',
    color: '#718096',
  },
  tableContainer: {
    overflowX: 'auto',
    marginTop: '16px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  th: {
    textAlign: 'left',
    padding: '12px',
    borderBottom: '2px solid #e2e8f0',
    color: '#4a5568',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  tr: {
    borderBottom: '1px solid #f0f0f0',
  },
  td: {
    padding: '12px',
    verticalAlign: 'top',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  patternId: {
    background: '#f0fff4',
    color: '#276749',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '12px',
  },
  periodsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  periodBadge: {
    background: '#ebf8ff',
    color: '#2b6cb0',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    whiteSpace: 'nowrap',
  },
  showMoreBtn: {
    marginTop: '16px',
    padding: '8px 16px',
    background: 'transparent',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    color: '#4a5568',
    cursor: 'pointer',
    fontSize: '13px',
  },
  uploadSection: {
    textAlign: 'center',
    padding: '20px 0',
  },
  uploadBtn: {
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: '600',
    background: '#3182ce',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  uploadBtnDisabled: {
    background: '#a0aec0',
    cursor: 'not-allowed',
  },
  statusMessage: {
    marginTop: '16px',
    padding: '12px 20px',
    borderRadius: '8px',
    fontSize: '14px',
  },
  successMessage: {
    background: '#f0fff4',
    color: '#276749',
    border: '1px solid #9ae6b4',
  },
  errorMessage: {
    background: '#fff5f5',
    color: '#c53030',
    border: '1px solid #feb2b2',
    marginTop: '12px',
    padding: '12px',
    borderRadius: '6px',
    fontSize: '14px',
  },
};

export default SchedulePatternUploader;